import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { leadAPI, Lead } from '../../services/crmAPI';
import CRMSubNav from './CRMSubNav';
import './CRM.css';

interface PipelineData {
  [key: string]: Lead[];
}

const PIPELINE_STAGES = [
  { key: 'NEW', label: 'New Leads', color: '#3b82f6' },
  { key: 'RESEARCHING', label: 'Researching', color: '#8b5cf6' },
  { key: 'CONTACTED', label: 'Contacted', color: '#06b6d4' },
  { key: 'MEETING_SCHEDULED', label: 'Meeting Scheduled', color: '#f59e0b' },
  { key: 'SAMPLED', label: 'Sent Samples', color: '#ec4899' },
  { key: 'NEGOTIATING', label: 'Negotiating', color: '#f97316' },
];

const Pipeline: React.FC = () => {
  const navigate = useNavigate();
  const [pipeline, setPipeline] = useState<PipelineData>({});
  const [loading, setLoading] = useState(true);
  const [draggedLead, setDraggedLead] = useState<Lead | null>(null);

  useEffect(() => {
    loadPipeline();
  }, []);

  const loadPipeline = async () => {
    try {
      setLoading(true);
      const response = await leadAPI.getPipeline();
      setPipeline(response.data);
    } catch (err) {
      console.error('Error loading pipeline:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleDragStart = (e: React.DragEvent, lead: Lead) => {
    setDraggedLead(lead);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDrop = async (e: React.DragEvent, newStatus: string) => {
    e.preventDefault();
    if (!draggedLead || draggedLead.status === newStatus) {
      setDraggedLead(null);
      return;
    }

    try {
      await leadAPI.update(draggedLead.id, { status: newStatus });
      loadPipeline();
    } catch (err) {
      console.error('Error updating lead status:', err);
    } finally {
      setDraggedLead(null);
    }
  };

  const handleDragEnd = () => {
    setDraggedLead(null);
  };

  const getLeadCount = (status: string) => {
    return pipeline[status]?.length || 0;
  };

  const formatDate = (dateStr: string | null | undefined) => {
    if (!dateStr) return '';
    return new Date(dateStr).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
    });
  };

  if (loading) {
    return (
      <div className="crm-page">
        <CRMSubNav />
        <div className="loading">Loading pipeline...</div>
      </div>
    );
  }

  return (
    <div className="crm-page">
      <CRMSubNav />

      <div className="crm-header">
        <h1>Sales Pipeline</h1>
        <div className="crm-header-actions">
          <Link to="/crm/leads/new" className="btn btn-primary">
            + Add Lead
          </Link>
        </div>
      </div>

      <div className="pipeline-container">
        {PIPELINE_STAGES.map(stage => (
          <div
            key={stage.key}
            className={`pipeline-column ${draggedLead && draggedLead.status !== stage.key ? 'drop-target' : ''}`}
            onDragOver={handleDragOver}
            onDrop={(e) => handleDrop(e, stage.key)}
          >
            <div className="pipeline-header" style={{ borderTopColor: stage.color }}>
              <h3>{stage.label}</h3>
              <span className="pipeline-count">{getLeadCount(stage.key)}</span>
            </div>
            <div className="pipeline-cards">
              {(pipeline[stage.key] || []).map(lead => (
                <div
                  key={lead.id}
                  className={`pipeline-card ${draggedLead?.id === lead.id ? 'dragging' : ''}`}
                  draggable
                  onDragStart={(e) => handleDragStart(e, lead)}
                  onDragEnd={handleDragEnd}
                  onClick={() => navigate(`/crm/leads/${lead.id}`)}
                >
                  <div className="pipeline-card-header">
                    <strong>{lead.business_name}</strong>
                  </div>
                  <div className="pipeline-card-meta">
                    {lead.city}, {lead.state}
                  </div>
                  <div className="pipeline-card-contact">
                    {lead.contact_name}
                  </div>
                  <div className="pipeline-card-footer">
                    {lead.assigned_to && (
                      <span className="pipeline-assigned">{lead.assigned_to}</span>
                    )}
                    {lead.next_follow_up && (
                      <span className={`pipeline-followup ${lead.is_follow_up_overdue ? 'overdue' : ''}`}>
                        {formatDate(lead.next_follow_up)}
                      </span>
                    )}
                  </div>
                </div>
              ))}
              {getLeadCount(stage.key) === 0 && (
                <div className="pipeline-empty">
                  No leads
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Won/Lost Summary */}
      <div className="pipeline-summary">
        <div className="summary-card won">
          <h4>Won</h4>
          <span className="summary-count">{getLeadCount('WON')}</span>
        </div>
        <div className="summary-card lost">
          <h4>Lost</h4>
          <span className="summary-count">{getLeadCount('LOST')}</span>
        </div>
        <div className="summary-card on-hold">
          <h4>On Hold</h4>
          <span className="summary-count">{getLeadCount('ON_HOLD')}</span>
        </div>
      </div>
    </div>
  );
};

export default Pipeline;
